import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Divider, Typography } from 'shared/ui'
import { PageNumberInfo } from 'shared/ui/PageNumberInfo/PageNumberInfo'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { AutoRechargeCard } from 'entities/Billing/ui/AutoRechargeWidget/ui/AutoRechargeCard'
import { CounterWithInfo } from 'pages/settings/pages/billing/pages/upgrade/ui/CounterWithInfo/CounterWithInfo'
import { DowngradeContent } from 'pages/settings/pages/billing/pages/upgrade/ui/DowngradeContent/DowngradeContent'
import { PhoneCheckr, PhoneCheckrStore } from 'pages/settings/ui/PhoneCheckr'
import { useUpgradeLayoutBackChrome } from 'pages/settings/pages/billing/pages/upgrade/hooks/useUpgradeLayoutBackChrome'
import { UpgradePeriodInfo } from 'pages/settings/pages/billing/pages/upgrade/ui/UpgradePeriodInfo/UpgradePeriodInfo'
import { useBillingUpgradePageContext } from 'pages/settings/pages/billing/pages/upgrade/context/upgradeContext'
import styles from './styles.module.scss'

export const UpgradeAddOnsPage = observer(() => {
  const store = useBillingUpgradePageContext()
  const entities = useEntitiesContext()
  const phoneCheckrStore = useMemo(() => new PhoneCheckrStore(entities), [entities])
  const { previousStep, handleAddOnsContinue, currentStep, totalSteps, isDowngrade } = store

  useUpgradeLayoutBackChrome(previousStep)

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerNav}>
          <PageNumberInfo
            currentStep={currentStep}
            totalSteps={totalSteps}
            title={isDowngrade ? 'Downgrade' : 'Upgrade'}
          />
        </div>
        <Typography
          ariaLabel='billing_upgrade_addons_page_title'
          className={styles.title}
          variant='body-xl-medium'
          tag='div'
          marginTop={36}
        >
          {isDowngrade ? 'Downgrade reason' : 'Customize with add-ons'}
        </Typography>
      </div>

      <div className={styles.content}>
        <UpgradePeriodInfo
          className={styles.periodTabs}
          period={store.period}
          handleSetPeriod={store.handleSetPeriod}
          isPlanLoading={store.isPlanLoading}
          annualSavings={store.annualSavings}
          shouldShowPeriodTabs={store.shouldShowPeriodTabs}
          selectedPlanInfo={null}
          isUpgradeModalV2_2={store.isUpgradeModalV2_2}
          showDivider={false}
        />

        {isDowngrade ? (
          <DowngradeContent store={store.downgradeContentStore} />
        ) : (
          <>
            <div className={styles.addOnsContainer}>
              <Typography
                ariaLabel='billing_upgrade_addons_additional_label'
                variant='body-md-regular'
                color='var(--content-primary-secondary)'
              >
                Additional
              </Typography>
              <CounterWithInfo
                icon='users'
                name='Seats'
                cost={store.additionalSeatPrice}
                counter={store.seats}
                changeCounter={store.changeSeatsCounter}
              />
              <Divider style={{ borderColor: 'var(--black-20)' }} />
              <CounterWithInfo
                icon='phone'
                name='Numbers'
                cost={store.additionalNumbersPrice}
                counter={store.numbers}
                changeCounter={store.changeNumbersCounter}
              />
            </div>

            {store.showAutoRechargeBlock && (
              <AutoRechargeCard
                store={store.autoRechargeWidgetStore}
                version='borderWithIcon'
                showFreeLabel
                className={styles.autoRechargeCard}
              />
            )}
            {store.showPhoneCheckrBlock && <PhoneCheckr store={phoneCheckrStore} />}
          </>
        )}

        <div className={styles.ctaWrapper}>
          <Button
            fullWidth
            text={isDowngrade ? 'Continue with downgrade' : 'Continue to checkout'}
            typeBtn='contained'
            contained='primary'
            size='extraLarge'
            intent='default'
            className={styles.ctaButton}
            onClick={handleAddOnsContinue}
            disabled={isDowngrade && store.downgradeContentStore.selectedReason === null}
          />
        </div>
      </div>
    </div>
  )
})
